import { Component, OnInit, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { showErrorSwal } from '@asamco/utils';
import { DxDataGridComponent, DxSwitchModule, DxDataGridModule, DxButtonModule } from 'devextreme-angular';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { LinkRunStatus } from '../../enums/link-run-status.enum';
import { LinkStatusCode } from '../../enums/link-status-code.enum';
import { DashboardItem } from '../../interfaces/dashboard-item.interface';
import { LinkStatus } from '../../interfaces/link-status.interface';
import { DashboardItemService } from '../../services/dashboard-item.service';
import { LinkStatusService } from '../../services/link-status.service';
import { LinkService } from '../../services/link.service';
import { LinkStatusSignalrService } from '../../services/signalR/link-status-signalr.service';
import { AdminLinkLogModalWrapperComponent } from '../admin-link-log-modal-wrapper/admin-link-log-modal-wrapper.component';
import { QueueService } from '../../services/queue.service';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoSelectionModule, DxoFilterRowModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { NgIf, NgClass, AsyncPipe, JsonPipe, DatePipe, NgStyle } from '@angular/common';
import { AdminLinkOverviewLinkIssueModalComponent } from '../admin-link-overview-link-issue-modal/admin-link-overview-link-issue-modal.component';
import { Router } from '@angular/router';

@Component({
	selector: 'app-shell-admin-link-display',
	templateUrl: './admin-link-display.component.html',
	styleUrls: ['../../styles/alert.scss','./admin-link-display.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		DxSwitchModule,
		NgIf,
		DxDataGridModule,
		DxoSelectionModule,
		DxoFilterRowModule,
		DxiColumnModule,
		DxTemplateModule,
		NgClass,
		AdminLinkOverviewLinkIssueModalComponent,
		AsyncPipe,
		DatePipe,
		NgStyle,
		JsonPipe,
		DxButtonModule
	],
})
export class AdminLinkDisplayComponent implements OnInit {
	@ViewChild(AdminLinkOverviewLinkIssueModalComponent)
	linkIssueModal: AdminLinkOverviewLinkIssueModalComponent;

	public enableLiveColumnRefresh: boolean = false;
	public listSubject: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
	public listObservable: Observable<DataSource> = this.listSubject.asObservable();
	private destroy$: Subject<void> = new Subject<void>();
	public selectedLinkId: number;

	linkStatusCode = LinkStatusCode;

	constructor(
		private dashboardItemService: DashboardItemService,
		private linkStatusSignalrService: LinkStatusSignalrService,
		private linkService: LinkService,
		private cdr: ChangeDetectorRef,
		private queueService: QueueService,
		private router: Router,
	) { }
	@ViewChild(DxDataGridComponent, { static: false }) dataGrid: DxDataGridComponent;

	ngOnInit(): void {
		this.getData();
		this.startSignalR();
	}

	private getData() {
		// Assuming the additional endpoint returns an array of objects with a `linkId` field
		this.dashboardItemService
			.adminGetAll()
			.pipe(
				switchMap((dashboardItems: DashboardItem[]) =>
					this.linkService.getAdminLinkInfoReport().pipe(
						map((linkIssues: any[]) => {
							// Merge dashboardItems with the data from links
							return dashboardItems.map((item: DashboardItem) => {
								const matchingIssue = linkIssues.filter(issue => issue.linkId === item.id);
								return {
									...item,
									livestatus: item.status,
									isLinkCurrentlyRunning: false,
									livestatuscolor: LinkStatusCode.Undefined,
									matchingLinkIssues: matchingIssue || null,
								};
							});
						})
					)
				)
			)
			.subscribe(data => {
				const jobDataSource = new DataSource({
					store: new ArrayStore({
						data: data,
						key: 'id',
					}),
				});
				this.listSubject.next(jobDataSource);
			});

	}

	public navigateToLink(linkId: number)
	{
		this.router.navigate(['application-link/link/' + linkId], { queryParams: { tab: 2 } });
	}

	public OpenLinkIssueScreen(linkIssues: any[])
	{
		this.linkIssueModal.open(linkIssues)
	}

	public get linkRunStatus(): typeof LinkRunStatus {
		return LinkRunStatus;
	}

	getDaysTillExpiry(expiryDate: string): { daysLeft: number, expired: boolean } {
		const today = new Date();
		const expiration = new Date(expiryDate);
		const diffInTime = expiration.getTime() - today.getTime();
		const daysLeft = Math.floor(diffInTime / (1000 * 60 * 60 * 24));

		return {
			daysLeft: Math.abs(daysLeft),
			expired: daysLeft < 0
		};
	}

	isExpired(expiryDate: string | Date): boolean {
		const currentDate = new Date();
		const expiry = new Date(expiryDate);
		return expiry < currentDate;  // Return true if the expiry date has passed
	}

	public startSignalR() {

		this.linkStatusSignalrService
			.getLinkButtonObservable()
			.pipe(takeUntil(this.destroy$))
			.subscribe(linkButtonStatus => {
				this.listSubject
					.getValue()
					.store()
					.update(linkButtonStatus.linkID, { isLinkCurrentlyRunning: linkButtonStatus.isLinkRunning })
					.then(_ => this.cdr.detectChanges());
			});

		this.linkStatusSignalrService
			.getLinkStatusObservable()
			.pipe(takeUntil(this.destroy$))
			.subscribe((linkStatus: LinkStatus) => {
				//Updating the current link status if the link received an signalR update, but only if the status is currently inactive
				this.listSubject
					.getValue()
					.store()
					.byKey(linkStatus.linkID)
					.then(
						dataItem => {
							if (!dataItem.isLinkCurrentlyRunning) {
								var lastExecutionDateObservable = this.linkService.getLinkLastExecutionDate(linkStatus.linkID);
								var isLinkRunningObservable = this.queueService.isLinkRunning(linkStatus.linkID);

								forkJoin([lastExecutionDateObservable, isLinkRunningObservable]).subscribe(data => {
									this.listSubject
										.getValue()
										.store()
										.update(linkStatus.linkID, { lastLogDate: data[0], isLinkCurrentlyRunning: data[1] })
										.then(item => {
											if (this.enableLiveColumnRefresh) {
												//Swap sort order so we can get the most recent active one on top
												this.dataGrid.instance.columnOption('isLinkCurrentlyRunning', {
													sortOrder: 'asc',
													sortIndex: 0,
												});
												this.dataGrid.instance.columnOption('isLinkCurrentlyRunning', {
													sortOrder: 'desc',
													sortIndex: 0,
												});
											}
											this.cdr.detectChanges();
										});
								});
							}
						},
						error => {
							showErrorSwal(error);
						}
					);

				this.listSubject
					.getValue()
					.store()
					.update(linkStatus.linkID, { livestatus: linkStatus.status, livestatuscolor: linkStatus.statusCode })
					.then(_ => this.cdr.detectChanges());
				// this.listSubject.getValue().store().update(linkStatus.linkID, { livestatuscolor: linkStatus.statusCode }).then();
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
